/**
 * Multi-tab chat thread state for the Care Delivery AI sidebar.
 *
 * Owns the set of open thread tabs, the active tab, and each tab's message
 * list. Supports starting a fresh thread, sending a turn (streamed via
 * `useChatStream`, then the resolved thread id + assistant text are folded back
 * in), resuming an existing thread into a tab, and reopening the originating
 * thread of a pending ingestion review **in a new tab** without disturbing the
 * active tab. Message content is held in memory only (HIPAA).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import apiUtils, { isApiError } from "../apiUtils";
import logger from "../utils/logger";
import { extractCitationsFromText } from "../components/blueprint/citationResolve";
import { DEFAULT_THREAD_TITLE, deriveThreadTitle } from "../utils/threadTitle";
import { useChatStream } from "./useChatStream";
import type {
  ChatMessage,
  ChatThread,
  ChatThreadDetail,
  SourceCitation,
} from "../types";

/** A single open chat tab. */
export interface ChatTab {
  /** Stable client tab id (independent of the server thread id). */
  tabId: string;
  /** The server thread id once resolved (null for an unsent new tab). */
  threadId: string | null;
  /** The tab's display title. */
  title: string;
  /** Whether the title was set by the provider (locks the auto-namer). */
  renamed: boolean;
  /** The tab's persisted message history. */
  messages: ChatMessage[];
  /**
   * Whether this tab's `messages` are loaded. Restore-on-mount lists threads
   * *without* their messages (a light query); a restored tab is loaded lazily
   * the first time it becomes active. Fresh/sent/reopened tabs are born loaded.
   */
  messagesLoaded: boolean;
}

const THREADS_ENDPOINT = "/api/blueprint/threads";

let tabCounter = 0;
/** Generate a process-unique client tab id. */
function nextTabId(): string {
  tabCounter += 1;
  return `tab-${tabCounter}`;
}

/** Build a fresh, empty chat tab. */
function freshTab(): ChatTab {
  return {
    tabId: nextTabId(),
    threadId: null,
    title: DEFAULT_THREAD_TITLE,
    renamed: false,
    messages: [],
    messagesLoaded: true,
  };
}

/** Build an unloaded tab from a restore listing (no messages yet). */
function tabFromListing(thread: ChatThread): ChatTab {
  return {
    tabId: nextTabId(),
    threadId: thread.id,
    title: thread.title ?? "Conversation",
    renamed: thread.renamed,
    messages: [],
    messagesLoaded: false,
  };
}

/**
 * Restore a patient's persisted threads into tabs (list only, no messages).
 *
 * Issues a *single* light fetch listing the patient's threads (no
 * `include_messages` over-fetch) so a reload surfaces prior conversations in
 * the dropdown without eagerly loading every message of every thread (a heavy,
 * connection-holding query) and without an N+1 per-thread fan-out. Each tab's
 * messages load lazily the first time it becomes active.
 *
 * @param patientId - The patient whose threads to restore.
 * @returns One (unloaded) tab per persisted thread (oldest-listed first), or
 *   `[]` on error / when the patient has no threads yet.
 */
async function restoreTabs(patientId: string): Promise<ChatTab[]> {
  const threads = await apiUtils.fetchData<ChatThread[]>(
    `${THREADS_ENDPOINT}?patient_id=${encodeURIComponent(patientId)}`,
  );
  if (isApiError(threads) || !Array.isArray(threads)) {
    logger.devOnly.warn("useChatTabs: thread restore failed");
    return [];
  }
  return threads.map(tabFromListing);
}

/** True for a never-used tab (no server thread, no messages). */
function isPristine(tab: ChatTab): boolean {
  return tab.threadId === null && tab.messages.length === 0;
}

/**
 * Merge restore-on-mount threads into the current tabs.
 *
 * Restore must surface the patient's full persisted history *without* clobbering
 * an in-progress tab. When the user has not yet acted (`touched` false) the
 * restored set replaces the lone pristine starter tab outright. Once the user
 * has acted, restored threads are *prepended* (oldest-listed first) ahead of the
 * user's tabs, skipping any thread already open (matched by `threadId`) and
 * dropping stale non-active pristine starter tabs — so the dropdown ends up
 * listing every saved thread plus whatever the user is working on, with no
 * duplicates. The active tab is always retained (even if still empty) so the
 * user's focus is never yanked away by a late-landing restore.
 *
 * @param prev - The current tabs.
 * @param restored - The freshly restored persisted threads (as tabs).
 * @param touched - Whether the user has interacted since mount.
 * @param activeTabId - The currently-active tab id (always retained).
 * @returns The merged tab list.
 */
function mergeRestored(
  prev: ChatTab[],
  restored: ChatTab[],
  touched: boolean,
  activeTabId: string,
): ChatTab[] {
  if (!touched) return restored;
  const openThreadIds = new Set(
    prev.map((t) => t.threadId).filter((id): id is string => id !== null),
  );
  const kept = prev.filter((t) => t.tabId === activeTabId || !isPristine(t));
  // Restored tabs are always bound to a persisted thread, so threadId is a
  // string here; skip any whose thread is already open to avoid a duplicate.
  const novel = restored.filter(
    (t) => !openThreadIds.has(t.threadId as string),
  );
  return [...novel, ...kept];
}

/** A locally-constructed message for an unsent turn (negative seq, blank id). */
function localMessage(
  threadId: string | null,
  role: "user" | "assistant",
  content: string,
  citations?: SourceCitation[],
): ChatMessage {
  const resolvedCitations =
    role === "assistant"
      ? (citations ?? extractCitationsFromText(content))
      : undefined;
  return {
    id: "",
    thread_id: threadId ?? "",
    seq: -1,
    role,
    content,
    citations: resolvedCitations,
    ingestion_run_id: null,
  };
}

/**
 * Refetch a thread's persisted messages after a turn (Break #1).
 *
 * The backend persists the full turn — including a `system` message carrying the
 * `ingestion_run_id` when the assistant proposed Blueprint changes. The frontend
 * only folds in a locally-built assistant bubble (blank id, `ingestion_run_id:
 * null`), so without this refetch the inline `ProposalDiffReview` would never
 * surface. Replacing the tab's messages with the persisted set fixes both the
 * missing pending-review panel and the blank-id/`seq:-1` local placeholders.
 *
 * @param threadId - The resolved server thread id.
 * @returns The persisted messages, or null when the fetch fails.
 */
async function refetchThreadMessages(
  threadId: string,
): Promise<{ messages: ChatMessage[]; title: string | null } | null> {
  const detail = await apiUtils.fetchData<ChatThreadDetail>(
    `${THREADS_ENDPOINT}/${encodeURIComponent(threadId)}`,
  );
  if (isApiError(detail)) {
    logger.devOnly.warn("useChatTabs: post-turn message refetch failed");
    return null;
  }
  return {
    messages: Array.isArray(detail?.messages) ? detail.messages : [],
    title: detail?.thread ? detail.thread.title : null,
  };
}

/** The result surface returned by {@link useChatTabs}. */
export interface UseChatTabsResult {
  /** All open tabs. */
  tabs: ChatTab[];
  /** The active tab id. */
  activeTabId: string;
  /** The streaming status of the active turn. */
  streaming: boolean;
  /** Assistant text being streamed into the active tab. */
  streamedText: string;
  /** Switch the active tab. */
  selectTab: (tabId: string) => void;
  /** Open a fresh, empty tab and activate it. */
  newTab: () => void;
  /**
   * Send a message on the active tab (creates/resumes its server thread).
   * Returns whether the turn produced new mutations or proposals.
   */
  send: (
    patientId: string,
    message: string,
    thinkingBudget?: number,
  ) => Promise<{ hasMutation: boolean }>;
  /**
   * Rename a tab's thread (locks the auto-namer). Defaults to the active tab.
   */
  rename: (title: string, tabId?: string) => Promise<void>;
  /**
   * Delete a tab's thread (server-side when persisted) and drop the tab. If the
   * deleted tab was active, switches to another tab or opens a fresh one.
   */
  deleteThread: (tabId: string) => Promise<void>;
  /**
   * Open a server thread in a tab and activate it. If the thread is already
   * open, its existing tab is refreshed and activated instead of duplicated;
   * the previously-active tab is otherwise preserved.
   */
  reopenInNewTab: (threadId: string) => Promise<void>;
}

/**
 * Manage the multi-tab Care Delivery AI conversation state.
 *
 * On mount (and whenever `patientId` changes) the patient's persisted threads
 * are restored into tabs via a single light list call, so switching tabs or
 * reloading the page shows prior conversations rather than an empty chat. Each
 * thread's messages load lazily the first time its tab becomes active.
 *
 * @param patientId - The active patient the conversation is scoped to.
 * @returns Tab state plus open/select/send/rename/reopen actions.
 */
export function useChatTabs(patientId: string): UseChatTabsResult {
  const initial = freshTab();
  const [tabs, setTabs] = useState<ChatTab[]>([initial]);
  const [activeTabId, setActiveTabId] = useState<string>(initial.tabId);
  const { status, streamedText, send: streamSend } = useChatStream();
  // True once the user has interacted with this patient's chat (sent a turn,
  // opened/deleted a tab). Restore-on-mount then *merges* rather than replaces,
  // so a slow thread-load never clobbers — nor permanently drops — the user's
  // in-progress tabs (the #28 flakiness was restore being discarded wholesale
  // the instant the user touched anything, wiping the visible thread history).
  const touchedRef = useRef(false);
  // Guards against a stale patient's restore landing after a patient switch.
  const restoreSeqRef = useRef(0);
  // Aborts the in-flight chat stream on a patient or thread switch (B2). A late
  // stream can't write into the wrong tab/patient.
  const abortRef = useRef<AbortController | null>(null);
  // Monotonic send sequence; a turn's fold is dropped when a newer turn (or a
  // patient/thread switch, which bumps this) has superseded it (B2 guard).
  const sendSeqRef = useRef(0);
  // Mirrors activeTabId so the restore merge (which runs outside render) can
  // read the live active id without the effect re-subscribing to it.
  const activeTabIdRef = useRef(activeTabId);
  useEffect(() => {
    activeTabIdRef.current = activeTabId;
  }, [activeTabId]);

  /** Abort any in-flight stream and supersede its turn (patient/thread switch). */
  const abortInFlight = useCallback((): void => {
    sendSeqRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  // Abort an in-flight turn when the active thread changes (thread switch, B2).
  useEffect(() => {
    return () => {
      abortInFlight();
    };
  }, [activeTabId, abortInFlight]);

  // Restore persisted threads + messages for the patient on mount / change.
  useEffect(() => {
    const seq = (restoreSeqRef.current += 1);
    let cancelled = false;
    // A patient switch must abort any stream still writing into the old tab.
    abortInFlight();
    void restoreTabs(patientId).then((restored) => {
      // Ignore a superseded (patient changed) or unmounted restore.
      if (cancelled || seq !== restoreSeqRef.current || restored.length === 0) {
        return;
      }
      setTabs((prev) =>
        mergeRestored(
          prev,
          restored,
          touchedRef.current,
          activeTabIdRef.current,
        ),
      );
      // Only auto-select a restored thread when the user has not yet acted; an
      // in-progress tab must keep focus.
      if (!touchedRef.current) setActiveTabId(restored[0].tabId);
    });
    return () => {
      cancelled = true;
    };
  }, [patientId, abortInFlight]);

  const patchTab = useCallback((tabId: string, patch: Partial<ChatTab>) => {
    setTabs((prev) =>
      prev.map((tab) => (tab.tabId === tabId ? { ...tab, ...patch } : tab)),
    );
  }, []);

  // Lazily load the active tab's messages the first time it is opened. Restore
  // lists threads without messages, so only the thread the user actually opens
  // pays for its (potentially large) history — and only once.
  useEffect(() => {
    const active = tabs.find((t) => t.tabId === activeTabId);
    if (!active || active.messagesLoaded || active.threadId === null) return;
    const threadId = active.threadId;
    let cancelled = false;
    void apiUtils
      .fetchData<ChatThreadDetail>(
        `${THREADS_ENDPOINT}/${encodeURIComponent(threadId)}`,
      )
      .then((detail) => {
        if (cancelled) return;
        if (isApiError(detail)) {
          logger.devOnly.warn("useChatTabs: lazy message load failed");
          return;
        }
        patchTab(active.tabId, {
          messages: detail.messages,
          messagesLoaded: true,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [tabs, activeTabId, patchTab]);

  const selectTab = useCallback((tabId: string) => setActiveTabId(tabId), []);

  const newTab = useCallback(() => {
    touchedRef.current = true;
    const tab = freshTab();
    setTabs((prev) => [...prev, tab]);
    setActiveTabId(tab.tabId);
  }, []);

  const send = useCallback(
    async (
      patientId: string,
      message: string,
      thinkingBudget?: number,
    ): Promise<{ hasMutation: boolean }> => {
      touchedRef.current = true;
      const target = activeTabId;
      const current = tabs.find((t) => t.tabId === target);
      const resumeId = current?.threadId ?? null;
      const priorRunIds = new Set(
        (current?.messages ?? [])
          .map((m) => m.ingestion_run_id)
          .filter((id): id is string => id !== null),
      );

      // Start a fresh abort scope for this turn; the captured seq lets the
      // post-stream writes detect they have been superseded (B2).
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const mySeq = (sendSeqRef.current += 1);
      const superseded = (): boolean => mySeq !== sendSeqRef.current;

      setTabs((prev) =>
        prev.map((tab) =>
          tab.tabId === target
            ? {
                ...tab,
                title: autoTitledFrom(tab, message),
                messages: [
                  ...tab.messages,
                  localMessage(tab.threadId, "user", message),
                ],
              }
            : tab,
        ),
      );

      let result = await streamSend({
        patientId,
        message,
        threadId: resumeId,
        signal: controller.signal,
        thinkingBudget,
      });
      // B5: a resumed thread that 404s is stale/deleted — retry once as a fresh
      // thread (no resume id) rather than surfacing a dead-end error. After the
      // retry the stale resume id must not be reused as a fallback.
      let fallbackThread = resumeId;
      if (result.staleThread && !superseded()) {
        fallbackThread = null;
        result = await streamSend({
          patientId,
          message,
          threadId: null,
          signal: controller.signal,
          thinkingBudget,
        });
      }

      // A superseded or aborted turn must not write into a tab the user has
      // navigated off of.
      if (result.aborted || superseded()) return { hasMutation: false };

      // Prefer the header-resolved thread id; fall back to the resume id (a
      // resumed thread whose header was absent), unless the resume id was stale.
      const resolvedThread = result.threadId ?? fallbackThread;
      setTabs((prev) =>
        prev.map((tab) =>
          tab.tabId === target
            ? foldAssistantTurn(tab, resolvedThread, result.text)
            : tab,
        ),
      );

      // Break #1: replace the local fold with the persisted thread (surfacing
      // the backend `system` message that carries `ingestion_run_id`, so the
      // inline ProposalDiffReview renders).
      if (resolvedThread === null) return { hasMutation: false };
      const persisted = await refetchThreadMessages(resolvedThread);
      if (persisted === null || superseded()) return { hasMutation: false };
      setTabs((prev) =>
        prev.map((tab) => {
          if (tab.tabId !== target) return tab;
          const patch: Partial<ChatTab> = {
            messages: persisted.messages,
            messagesLoaded: true,
            threadId: resolvedThread,
          };
          if (!tab.renamed && persisted.title) {
            patch.title = persisted.title;
          }
          return { ...tab, ...patch };
        }),
      );

      const hasMutation = (persisted.messages ?? []).some(
        (m) =>
          m.ingestion_run_id !== null && !priorRunIds.has(m.ingestion_run_id),
      );
      return { hasMutation };
    },
    [activeTabId, tabs, streamSend],
  );

  const rename = useCallback(
    async (title: string, tabId?: string): Promise<void> => {
      const target = tabId ?? activeTabId;
      const tab = tabs.find((t) => t.tabId === target);
      if (!tab) return;
      if (!tab.threadId) {
        patchTab(target, { title, renamed: true });
        return;
      }
      const resp = await apiUtils.fetchData<ChatThread>(
        `${THREADS_ENDPOINT}/${encodeURIComponent(tab.threadId)}`,
        "PATCH",
        { title },
      );
      if (isApiError(resp)) {
        logger.devOnly.warn("useChatTabs: rename failed");
        return;
      }
      patchTab(target, { title: resp.title ?? title, renamed: true });
    },
    [tabs, activeTabId, patchTab],
  );

  const deleteThread = useCallback(
    async (tabId: string): Promise<void> => {
      touchedRef.current = true;
      const tab = tabs.find((t) => t.tabId === tabId);
      if (!tab) return;
      if (tab.threadId) {
        const resp = await apiUtils.fetchData(
          `${THREADS_ENDPOINT}/${encodeURIComponent(tab.threadId)}`,
          "DELETE",
        );
        if (isApiError(resp)) {
          logger.devOnly.warn("useChatTabs: delete failed");
          return;
        }
      }
      setTabs((prev) => {
        const remaining = prev.filter((t) => t.tabId !== tabId);
        const next = remaining.length > 0 ? remaining : [freshTab()];
        setActiveTabId((active) => (active === tabId ? next[0].tabId : active));
        return next;
      });
    },
    [tabs],
  );

  const reopenInNewTab = useCallback(
    async (threadId: string): Promise<void> => {
      touchedRef.current = true;
      const detail = await apiUtils.fetchData<ChatThreadDetail>(
        `${THREADS_ENDPOINT}/${encodeURIComponent(threadId)}`,
      );
      if (isApiError(detail)) {
        logger.devOnly.warn("useChatTabs: reopen failed");
        return;
      }
      const newTab: ChatTab = {
        tabId: nextTabId(),
        threadId: detail.thread.id,
        title: detail.thread.title ?? "Conversation",
        renamed: detail.thread.renamed,
        messages: detail.messages,
        messagesLoaded: true,
      };
      // If the thread already has an open tab, refresh its messages/title in
      // place and activate it (no duplicate); otherwise append the new tab. The
      // previously-active tab is preserved either way.
      setTabs((prev) => {
        const existing = prev.find((t) => t.threadId === detail.thread.id);
        if (existing) {
          setActiveTabId(existing.tabId);
          return prev.map((t) =>
            t.tabId === existing.tabId
              ? {
                  ...t,
                  title: newTab.title,
                  renamed: newTab.renamed,
                  messages: newTab.messages,
                  messagesLoaded: true,
                }
              : t,
          );
        }
        setActiveTabId(newTab.tabId);
        return [...prev, newTab];
      });
    },
    [],
  );

  return {
    tabs,
    activeTabId,
    streaming: status === "streaming",
    streamedText,
    selectTab,
    newTab,
    send,
    rename,
    deleteThread,
    reopenInNewTab,
  };
}

/**
 * The tab's title after (maybe) auto-naming it from its first user message.
 *
 * Mirrors the backend `_maybe_auto_name` guard (`services/chat/threads.py`): a
 * title is derived from the message only while the tab still carries the default
 * placeholder and the provider has not renamed it (the `renamed` lock). Because
 * the guard only fires while the title is still {@link DEFAULT_THREAD_TITLE}, a
 * second/later turn never re-titles, and a manual rename is never clobbered —
 * matching what the server persists, so the live title equals the post-reload
 * one.
 *
 * @param tab - The tab receiving the user message.
 * @param message - The user message content to derive a title from.
 * @returns The derived title when the auto-namer applies, else the current title.
 */
function autoTitledFrom(tab: ChatTab, message: string): string {
  if (tab.renamed || tab.title !== DEFAULT_THREAD_TITLE) return tab.title;
  return deriveThreadTitle(message);
}

/**
 * Fold a completed assistant turn into a tab's state.
 *
 * @param tab - The tab the turn belongs to.
 * @param resolvedThread - The resolved thread id (may be null for a fresh tab).
 * @param assistantText - The assistant's final text.
 * @returns The updated tab.
 */
function foldAssistantTurn(
  tab: ChatTab,
  resolvedThread: string | null,
  assistantText: string,
): ChatTab {
  const citations = extractCitationsFromText(assistantText);
  return {
    ...tab,
    threadId: resolvedThread ?? tab.threadId,
    messages: [
      ...tab.messages,
      localMessage(tab.threadId, "assistant", assistantText, citations),
    ],
  };
}
